<template>
  <div class="domains-tab mx-2 mb-4">
    <div class="alert alert-light border small mb-3 py-2">
      <strong>Domains</strong> — prospecting domains on active clients with at least one active
      mailbox. DNS/HTTPS are probed live (not the DB <code>https</code> flag).
      <strong>A → redirect server</strong> means the A record points to
      <code>52.211.161.236</code> (HTTPS redirect + cert). Domains without A are listed for
      DNS/ops follow-up; HTTPS is only generated for domains that already point to that server.
    </div>

    <div class="card border-0 shadow-sm mb-3">
      <div class="card-body py-2 d-flex flex-wrap align-items-center">
        <div class="d-flex align-items-center mr-3 mb-1">
          <span class="small text-muted mr-2">Filter</span>
          <select
            v-model="filter"
            class="form-control form-control-sm"
            style="width: 200px;"
          >
            <option value="all">all</option>
            <option value="no_a">no A record</option>
            <option value="a_server">A → redirect server</option>
            <option value="a_other">A → other IP</option>
            <option value="https_ok">HTTPS ok</option>
            <option value="https_no">HTTPS fail</option>
            <option value="pending">probe pending</option>
          </select>
        </div>
        <div class="d-flex align-items-center mr-3 mb-1">
          <span class="small text-muted mr-2">Search</span>
          <input
            v-model="search"
            type="text"
            class="form-control form-control-sm"
            style="width: 220px;"
            placeholder="domain / client"
          />
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary mb-1 mr-2"
          :disabled="loading"
          @click="loadItems"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm mr-1" />
          Refresh list
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary mb-1"
          :disabled="loading || probing || !items.length"
          @click="probeAll"
        >
          <span v-if="probing" class="spinner-border spinner-border-sm mr-1" />
          {{ probing ? "Probing… " + probedCount + "/" + items.length : "Re-probe DNS/HTTPS" }}
        </button>
        <span v-if="summaryText" class="small text-muted ml-auto mb-1">
          {{ summaryText }}
        </span>
      </div>
    </div>

    <LoadingSpinner v-if="loading" title="Loading domains…" />

    <p v-else-if="!filteredItems.length" class="text-muted small mb-0">
      No domains for these filters.
    </p>

    <div v-else class="table-responsive">
      <table class="table table-sm table-hover bg-white border">
        <thead class="thead-light">
          <tr>
            <th style="width: 40px;">#</th>
            <th>Domain</th>
            <th>Created</th>
            <th>Client</th>
            <th>Workspace</th>
            <th>Mailboxes</th>
            <th>A record</th>
            <th>HTTPS</th>
            <th>DB flags</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in filteredItems"
            :key="row.workspace_domain_id"
            :class="rowRowClass(row)"
          >
            <td class="text-muted">{{ index + 1 }}</td>
            <td>
              <a
                :href="'https://' + row.domain"
                target="_blank"
                rel="noopener"
                class="font-weight-bold"
              >{{ row.domain }}</a>
              <div v-if="row.purchased_in" class="text-muted" style="font-size: 11px;">
                purchased_in: {{ row.purchased_in }}
              </div>
            </td>
            <td class="small text-nowrap">
              {{ formatDate(row.domain_created) }}
            </td>
            <td>
              <a
                v-if="row.client_id"
                :href="'/clients/' + row.client_id"
                target="_blank"
              >{{ row.company_name || "—" }}</a>
              <span v-else>{{ row.company_name || "—" }}</span>
              <div class="text-muted" style="font-size: 11px;">
                #{{ row.client_id }} · {{ row.client_status }}
              </div>
            </td>
            <td>{{ row.workspace || "—" }}</td>
            <td>{{ row.active_mailboxes }}</td>
            <td>
              <span
                v-if="row.probe === 'pending'"
                class="spinner-border spinner-border-sm text-muted"
              />
              <template v-else>
                <span class="badge" :class="aBadgeClass(row)">
                  {{ aLabel(row) }}
                </span>
                <div v-if="row.a_ip" class="text-muted" style="font-size: 11px;">
                  {{ row.a_ip }}
                </div>
              </template>
            </td>
            <td>
              <span
                v-if="row.probe === 'pending'"
                class="spinner-border spinner-border-sm text-muted"
              />
              <i
                v-else-if="row.https === 'ok'"
                class="icon-check text-success"
                title="HTTPS ok"
              />
              <i
                v-else
                class="icon-close text-danger"
                title="HTTPS fail"
              />
            </td>
            <td class="small text-muted">
              https={{ row.https_flag || "0" }}
              · cloud_dns={{ row.cloud_dns || "0" }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { workspaceService } from "@/_services";

const REDIRECT_SERVER_IP = "52.211.161.236";
const PROBE_CONCURRENCY = 8;

export default {
  name: "DomainsTab",
  components: {
    LoadingSpinner,
  },
  data() {
    return {
      loading: false,
      probing: false,
      items: [],
      filter: "all",
      search: "",
      probedCount: 0,
    };
  },
  computed: {
    filteredItems() {
      const q = (this.search || "").trim().toLowerCase();
      return this.items.filter((row) => {
        if (this.filter === "no_a" && row.a_status !== "no_a") return false;
        if (this.filter === "a_server" && row.a_status !== "a_server") return false;
        if (this.filter === "a_other" && row.a_status !== "a_other") return false;
        if (this.filter === "https_ok" && row.https !== "ok") return false;
        if (this.filter === "https_no" && !(row.probe === "done" && row.https !== "ok")) {
          return false;
        }
        if (this.filter === "pending" && row.probe !== "pending") return false;
        if (!q) return true;
        const hay = [
          row.domain,
          row.company_name,
          row.client_id,
          row.workspace,
          row.a_ip,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    },
    summaryText() {
      if (!this.items.length) return "";
      const total = this.items.length;
      const onServer = this.items.filter((r) => r.a_status === "a_server").length;
      const noA = this.items.filter((r) => r.a_status === "no_a").length;
      const other = this.items.filter((r) => r.a_status === "a_other").length;
      const httpsOk = this.items.filter((r) => r.https === "ok").length;
      const pending = this.items.filter((r) => r.probe === "pending").length;
      return (
        `${total} domains · A→server ${onServer} · no A ${noA} · other IP ${other}` +
        ` · HTTPS ok ${httpsOk}` +
        (pending ? ` · probing ${pending}` : "")
      );
    },
  },
  created() {
    this.loadItems();
  },
  methods: {
    async loadItems() {
      this.loading = true;
      this.probing = false;
      try {
        const result = await workspaceService.getProspectingDomainsDns();
        const rows = Array.isArray(result)
          ? result
          : (result && result.result) || [];
        this.items = rows.map((r) => ({
          ...r,
          probe: "pending",
          https: null,
          a_ip: null,
          a_status: "pending",
        }));
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
      if (this.items.length) {
        this.probeAll();
      }
    },
    async probeAll() {
      if (!this.items.length || this.probing) return;
      this.probing = true;
      this.probedCount = 0;
      this.items = this.items.map((r) => ({
        ...r,
        probe: "pending",
        https: null,
        a_ip: null,
        a_status: "pending",
      }));

      let index = 0;
      const runNext = async () => {
        while (index < this.items.length) {
          const i = index;
          index += 1;
          await this.probeOne(this.items[i], i);
          this.probedCount += 1;
        }
      };

      const workers = [];
      const n = Math.min(PROBE_CONCURRENCY, this.items.length);
      for (let w = 0; w < n; w += 1) {
        workers.push(runNext());
      }
      await Promise.all(workers);
      this.probing = false;
    },
    async probeOne(row, index) {
      try {
        const result = await workspaceService.getHttps(row.domain);
        const dns = result && result.dns;
        const ip =
          Array.isArray(dns) && dns[0] && dns[0].ip ? String(dns[0].ip) : null;
        let aStatus = "no_a";
        if (ip === REDIRECT_SERVER_IP) aStatus = "a_server";
        else if (ip) aStatus = "a_other";

        this.$set(this.items, index, {
          ...this.items[index],
          probe: "done",
          https: result && result.https === "ok" ? "ok" : "no",
          a_ip: ip,
          a_status: aStatus,
        });
      } catch (e) {
        this.$set(this.items, index, {
          ...this.items[index],
          probe: "done",
          https: "no",
          a_ip: null,
          a_status: "no_a",
        });
      }
    },
    aLabel(row) {
      if (row.a_status === "a_server") return "A → server";
      if (row.a_status === "a_other") return "A → other";
      return "no A";
    },
    aBadgeClass(row) {
      if (row.a_status === "a_server") return "badge-success";
      if (row.a_status === "a_other") return "badge-warning";
      return "badge-danger";
    },
    rowRowClass(row) {
      if (row.a_status === "no_a") return "table-danger";
      if (row.a_status === "a_other") return "table-warning";
      if (row.https === "ok" && row.a_status === "a_server") return "table-success";
      return "";
    },
    formatDate(value) {
      if (!value) return "—";
      const raw = String(value).replace(" ", "T");
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(value);
      const pad = (n) => (n < 10 ? "0" + n : String(n));
      return (
        d.getFullYear() +
        "-" +
        pad(d.getMonth() + 1) +
        "-" +
        pad(d.getDate()) +
        " " +
        pad(d.getHours()) +
        ":" +
        pad(d.getMinutes())
      );
    },
  },
};
</script>
